import React from 'react';
import { useTheme } from '../../ThemeContext';

/**
 * Role Information Popup Component
 * Displays detailed information about staff role hierarchy
 * 
 * @param {boolean} visible - Controls popup visibility
 * @param {Function} onClose - Function to close the popup
 * @param {string} userRole - Current user's role for context
 * @param {Array} allowedRoles - Roles that current user can see/manage
 */
const RoleInfoPopup = ({ visible, onClose, userRole, allowedRoles = [] }) => {
  const { themeMode, currentTheme } = useTheme();

  if (!visible) return null;

  // Theme-based styling
  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10000,
    animation: 'fadeIn 0.3s ease-out'
  };

  const popupStyle = {
    backgroundColor: themeMode === 'dark' ? '#04002b' : '#ffffff',
    color: currentTheme.homePage.textColor,
    borderRadius: '16px',
    padding: '0',
    maxWidth: '700px',
    width: '90%',
    maxHeight: '90vh',
    overflow: 'hidden',
    boxShadow: themeMode === 'dark' 
      ? '0 20px 40px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.1)'
      : '0 20px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
    border: themeMode === 'dark' ? '1px solid #302d4c' : '1px solid #e9e8ea',
    animation: 'slideIn 0.3s ease-out',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif"
  };

  const headerStyle = {
    padding: '24px 24px 0 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: `1px solid ${themeMode === 'dark' ? '#302d4c' : '#e9e8ea'}`,
    paddingBottom: '16px',
    marginBottom: '24px'
  };

  const titleStyle = {
    fontSize: '22px',
    fontWeight: '600',
    color: currentTheme.homePage.textColor,
    margin: '0',
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  };

  const closeButtonStyle = {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    color: currentTheme.homePage.textColor,
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
    opacity: 0.7,
    transition: 'opacity 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px'
  };

  const contentStyle = {
    padding: '0 24px',
    marginBottom: '32px',
    maxHeight: '60vh',
    overflow: 'auto'
  };

  const roleCardStyle = (isCurrentRole) => ({
    backgroundColor: isCurrentRole 
      ? (themeMode === 'dark' ? '#1a237e20' : '#e3f2fd') 
      : (themeMode === 'dark' ? '#302d4c20' : '#f8f9fa'),
    border: isCurrentRole 
      ? (themeMode === 'dark' ? '2px solid #3f51b5' : '2px solid #2196f3')
      : `1px solid ${themeMode === 'dark' ? '#302d4c' : '#e9e8ea'}`,
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '16px',
    transition: 'all 0.2s ease'
  });

  const roleTitleStyle = (color) => ({
    fontSize: '18px',
    fontWeight: '600',
    color: color,
    margin: '0 0 8px 0',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  });

  const roleSubtitleStyle = {
    fontSize: '14px',
    color: currentTheme.homePage.textColor,
    opacity: 0.7,
    margin: '0 0 12px 0',
    fontStyle: 'italic'
  };

  const roleDescStyle = {
    fontSize: '14px',
    lineHeight: '1.5',
    color: currentTheme.homePage.textColor,
    margin: '8px 0'
  };

  const listStyle = {
    margin: '8px 0 0 16px',
    padding: '0'
  };

  const listItemStyle = {
    fontSize: '14px',
    lineHeight: '1.4',
    color: currentTheme.homePage.textColor,
    marginBottom: '4px'
  };

  const footerStyle = {
    padding: '0 24px 24px 24px',
    display: 'flex',
    justifyContent: 'flex-end'
  };

  const buttonStyle = {
    padding: '12px 24px',
    borderRadius: '8px',
    border: 'none',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minWidth: '80px',
    height: '44px',
    backgroundColor: themeMode === 'dark' ? '#302d4c' : '#e9e8ea',
    color: currentTheme.homePage.textColor
  };

  const getRoleIcon = (role) => {
    switch(role) {
      case 'admin':
        return '';
      case 'supervisor':
        return '';
      case 'user':
        return '';
      default:
        return '';
    }
  };

  const getRoleColor = (role) => {
    return themeMode === 'dark' ? '#ffffff' : '#000000'; // White in dark theme, black in light theme
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideIn {
            from { 
              opacity: 0; 
              transform: translateY(-20px) scale(0.95); 
            }
            to { 
              opacity: 1; 
              transform: translateY(0) scale(1); 
            }
          }
        `}
      </style>
      <div style={overlayStyle} onClick={onClose}>
        <div style={popupStyle} onClick={(e) => e.stopPropagation()}>
          <div style={headerStyle}>
            <h2 style={{...titleStyle, color: themeMode === 'dark' ? '#ffffff' : '#000000'}}>
              Staff Role Hierarchy
            </h2>
            <button 
              style={closeButtonStyle}
              onClick={onClose}
              onMouseEnter={(e) => e.target.style.opacity = '1'}
              onMouseLeave={(e) => e.target.style.opacity = '0.7'}
            >
              ×
            </button>
          </div>
          
          <div style={contentStyle}>
            <p style={{ ...roleDescStyle, marginBottom: '20px', fontWeight: '500' }}>
              Our healthcare platform uses a three-level role system to manage staff access and permissions across different facilities.
            </p>

            {/* Admin Role */}
            {allowedRoles.includes('admin') && (
              <div style={roleCardStyle(userRole === 'admin')}>
                <h3 style={roleTitleStyle(getRoleColor('admin'))}>
                  {getRoleIcon('admin')} Admin: Organization Administrator (Level 3)
                </h3>
                <p style={roleSubtitleStyle}>
                  Central team members overseeing all locations
                </p>
                <p style={roleDescStyle}>
                  <strong>What they can do:</strong>
                </p>
                <ul style={listStyle}>
                  <li style={listItemStyle}>Manage all staff across all facilities</li>
                  <li style={listItemStyle}>Create new admins, supervisors, and users</li>
                  <li style={listItemStyle}>Assign staff to any facility</li>
                  <li style={listItemStyle}>View and manage billing, facilities, and providers</li>
                  <li style={listItemStyle}>Access full customer details and enterprise settings</li>
                </ul>
              </div>
            )}

            {/* Supervisor Role */}
            {allowedRoles.includes('supervisor') && (
              <div style={roleCardStyle(userRole === 'supervisor')}>
                <h3 style={roleTitleStyle(getRoleColor('supervisor'))}>
                  {getRoleIcon('supervisor')} Supervisor: Location Manager (Level 2)
                </h3>
                <p style={roleSubtitleStyle}>
                  On-site managers responsible for specific clinic or hospital location
                </p>
                <p style={roleDescStyle}>
                  <strong>What they can do:</strong>
                </p>
                <ul style={listStyle}>
                  <li style={listItemStyle}>Manage users within their assigned facility only</li>
                  <li style={listItemStyle}>Create new users for their location</li>
                  <li style={listItemStyle}>View and manage staff at their own facility</li>
                  <li style={listItemStyle}>Update their own basic profile info</li>
                  <li style={listItemStyle}>Cannot assign themselves to other facilities or change their role</li>
                </ul>
              </div>
            )}

            {/* User Role */}
            {allowedRoles.includes('user') && (
              <div style={roleCardStyle(userRole === 'user')}>
                <h3 style={roleTitleStyle(getRoleColor('user'))}>
                  {getRoleIcon('user')} User: Basic Staff Member (Level 1)
                </h3>
                <p style={roleSubtitleStyle}>
                  Operational team members like nurses, doctors, or support staff
                </p>
                <p style={roleDescStyle}>
                  <strong>What they can do:</strong>
                </p>
                <ul style={listStyle}>
                  <li style={listItemStyle}>Update their own personal profile only</li>
                  <li style={listItemStyle}>Use tools for daily work (validation, call records, batch uploads)</li>
                  <li style={listItemStyle}>Access features limited to their assigned facility</li>
                  <li style={listItemStyle}>Cannot manage or view other staff</li>
                  <li style={listItemStyle}>Cannot access admin or billing information</li>
                </ul>
              </div>
            )}

            <div style={{ 
              backgroundColor: themeMode === 'dark' ? '#302d4c20' : '#f0f8ff', 
              padding: '16px', 
              borderRadius: '8px',
              border: `1px solid ${themeMode === 'dark' ? '#302d4c' : '#e9e8ea'}`,
              marginTop: '20px'
            }}>
              <p style={{ ...roleDescStyle, margin: '0', fontWeight: '500' }}>
                <strong>Need role changes?</strong> Only Admins can promote, demote, or reassign staff. Contact your organization's Admin for access modifications.
              </p>
            </div>
          </div>
          
          <div style={footerStyle}>
            <button
              style={buttonStyle}
              onClick={onClose}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = themeMode === 'dark' ? '#404040' : '#d0d0d0';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = themeMode === 'dark' ? '#302d4c' : '#e9e8ea';
              }}
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default RoleInfoPopup; 